'use client';

import { useState, type FormEvent } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import { Button, FormField, Input } from '@spark/ui';
import { mapAuthError } from '@/lib/auth/errors';
import { AUTH_TOAST_STYLE } from '@/components/auth/AdminAuthHeroLayout';
import { ROUTES, sanitizeRedirectAfter } from '@/lib/routes';
import { bffFetch } from '@/lib/api/bffFetch';

export function SignInEmailForm() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const redirectAfter = sanitizeRedirectAfter(searchParams.get('redirectAfter'));

  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string | undefined>>({});
  const [toast, setToast] = useState<string | undefined>();

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (submitting || !email.trim()) return;
    setSubmitting(true);
    setErrors({});
    setToast(undefined);

    try {
      const res = await bffFetch('/api/v1/public/auth/code-request', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ emailOrPhone: email, channel: 'EMAIL', redirectAfter }),
      });

      // Per A.1.1 + D-061: surface always navigates forward so account presence isn't leaked.
      if (res.ok || res.status === 404) {
        const params = new URLSearchParams({ step: 'verify', identity: email, redirectAfter });
        router.push(`${ROUTES.signIn}?${params.toString()}`);
        return;
      }

      const body = await safeJson(res);
      const mapped = mapAuthError(body, res.status);
      if (mapped.inline) setErrors({ [mapped.inline.field ?? '_form']: mapped.inline.message });
      if (mapped.toast) setToast(mapped.toast.message);
    } catch {
      setToast('Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <form onSubmit={onSubmit} className="flex flex-col gap-4" noValidate aria-busy={submitting || undefined}>
      <FormField label="Email" required errorText={errors.email} tone="on-dark">
        <Input
          tone="on-dark"
          type="email"
          name="email"
          autoComplete="email"
          placeholder="you@example.mil"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          disabled={submitting}
        />
      </FormField>

      {toast ? (
        <div role="alert" className="rounded-xl px-3 py-2 text-body-sm" style={AUTH_TOAST_STYLE}>
          {toast}
        </div>
      ) : null}

      <Button type="submit" size="lg" fullWidth disabled={submitting || !email.trim()} aria-busy={submitting || undefined}>
        {submitting ? 'Sending code…' : 'Send code'}
      </Button>
    </form>
  );
}

async function safeJson(res: Response): Promise<unknown> {
  try {
    return await res.json();
  } catch {
    return null;
  }
}
